<template id="AskForVolunteerButton">
    <CButton v-if="requestPending" disabled full size="large"> Žiadosť čaká na odpoveď </CButton>

    <CButton v-else-if="accepted" disabled full size="large"> Už si členom </CButton>

    <CButton v-else-if="rejected" disabled full size="large"> Žiadosť bola zamietnutá </CButton>

    <CButton v-else full size="large" variant="success" @click.prevent="openMembershipRequest">
        Chcem sa prihlásiť
    </CButton>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useModal } from 'vue-final-modal'
import { useExponea } from '@/composables/exponea/useExponea'
import { useFirebase } from '@/composables/firebase/useFirebase'

import { MembershipRequestStatus } from '#shared/types/MembershipRequest'
import type { MembershipRequest } from '#shared/types/MembershipRequest'
import type { Skill, SkillOrgPreview } from '#shared/types/Skill'
import { useMembershipRequests } from '@/composables/membershipRequests/useMembershipRequests'
import SignInModal from '@/components/modals/SignInModal.vue'
import RegistrationNotCompletedModal from '@/components/common/modals/RegistrationNotCompletedModal.vue'
import AskForMembershipModal from '@/components/skills/modals/AskForMembershipModal.vue'

interface Props {
    skill: Skill
    org: SkillOrgPreview
}

const props = defineProps<Props>()
const store = useApplicationStore()
const { auth } = useFirebase()
const { trackExponeaEvent } = useExponea()
const { getMembershipRequest } = useMembershipRequests()
const { gtag } = useGtag()

// Modal setup
const { open: openSignInModal } = useModal({
    component: SignInModal
})

const { open: openRegistrationNotCompletedModal } = useModal({
    component: RegistrationNotCompletedModal
})

const { open: openAskForMembershipModal, close: closeAskForMembershipModal } = useModal({
    component: AskForMembershipModal,
    attrs: {
        skillId: props.skill.id,
        onClose(result?: any) {
            closeAskForMembershipModal()
            if (result && typeof result === 'object' && 'id' in result) {
                request.value = result as MembershipRequest
            }
        }
    }
})

const request = ref<MembershipRequest | undefined>(undefined)

const requestPending = computed(() => {
    return Boolean(request.value?.status === MembershipRequestStatus.REQUESTED)
})

const accepted = computed(() => {
    return Boolean(request.value?.status === MembershipRequestStatus.ACCEPTED)
})

const rejected = computed(() => {
    return Boolean(request.value?.status === MembershipRequestStatus.REJECTED)
})

onMounted(async () => {
    if (store.userId) {
        request.value = await getMembershipRequest(store.userId, props.skill.id)
    }
})

async function openMembershipRequest() {
    gtag('event', 'openMembershipModal', {
        event_category: 'skill'
    })

    trackExponeaEvent({
        eventName: 'cta1_zaujem_o_clenstvo_v_skupine',
        userId: store.userId || '',
        eventProperties: {
            type: props.skill.type,
            title: props.skill.title,
            org_title: props.org.title,
            link: props.skill.studyLink
        }
    })

    if (!auth?.currentUser) {
        localStorage.setItem('beforeLogInLocation', window.location.pathname)
        return openSignInModal()
    }

    if (!auth.currentUser?.emailVerified || !store.userPrivateProfile?.rulesAccepted) {
        return openRegistrationNotCompletedModal()
    }

    await openAskForMembershipModal()
}
</script>
